import { Link } from "@tanstack/react-router";
import { Facebook, Mail, Phone, MapPin } from "lucide-react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { logoImage } from "@/lib/images";
import { NAV, SITE } from "@/lib/site";

const footerLinks = [...NAV.services, ...NAV.primary] as const;

export function SiteFooter() {
  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="diag-stripes h-2 w-full" />
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 lg:grid-cols-4 lg:px-8">
        <div>
          <div className="flex items-center gap-3">
            <ResponsiveImage
              {...logoImage}
              alt={`${SITE.shortName} logo`}
              className="h-14 w-14 rounded-full ring-2 ring-accent"
              loading="lazy"
            />
            <div>
              <div className="font-display text-xl text-accent">{SITE.name}</div>
              <div className="text-xs uppercase tracking-widest text-secondary-foreground/70">
                Since {SITE.foundingYear}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-secondary-foreground/80">
            Coast-to-coast long-haul transportation. Produce, dry goods, and general freight —
            delivered on time, every time.
          </p>
        </div>
        <nav aria-label="Footer">
          <p className="font-display text-sm tracking-widest text-accent">Navigate</p>
          <ul className="mt-4 space-y-2 text-sm text-secondary-foreground/80">
            <li>
              <Link
                to="/"
                className="hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Home
              </Link>
            </li>
            {footerLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div>
          <p className="font-display text-sm tracking-widest text-accent">Contact</p>
          <ul className="mt-4 space-y-3 text-sm text-secondary-foreground/80">
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-accent" aria-hidden="true" />{" "}
              <a href={`tel:${SITE.phoneTel}`}>{SITE.phoneDisplay}</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-accent" aria-hidden="true" />{" "}
              <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-accent" aria-hidden="true" /> {SITE.location}
            </li>
          </ul>
        </div>
        <div>
          <p className="font-display text-sm tracking-widest text-accent">Follow</p>
          <a
            href={SITE.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-md border border-accent/40 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Facebook className="h-4 w-4" aria-hidden="true" /> Facebook
          </a>
        </div>
      </div>
      <div className="border-t border-border/30 py-4 text-center text-xs text-secondary-foreground/70">
        © 2026 {SITE.name}. All rights reserved.
      </div>
    </footer>
  );
}
